import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  SimpleGrid,
  Button,
  Input,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Select as ChakraSelect,
  useToast,
  Switch,
  Tooltip,
  Text,
  IconButton,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import Select from "react-select";
import { cqAPI } from "../../lib/Auth";
import EditableText from "../EditableText/EditableText";
import {
  adminPriceLabel,
  adminFixedPriceOf,
  describeLicensedTiers,
  isLicensedPrice,
  isTieredPrice,
  isPerLocationAddonPrice,
  perLocationPriceLabel,
} from "./priceFormat";

function Addons() {
  const [addons, setAddons] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [products, setProducts] = useState([]);
  const [newAddon, setNewAddon] = useState({
    name: "",
    description: "",
    items: [],
    bundles: [],
  });
  const { isOpen, onOpen, onClose } = useDisclosure({
    onClose: () =>
      setNewAddon({ name: "", description: "", items: [], bundles: [] }),
  });
  const toast = useToast();

  const fetchAddons = async () => {
    const response = await cqAPI.get("/settings/subscriptions/addons");
    setAddons(response?.data?.addons ?? []);
  };

  const fetchBundles = async () => {
    const response = await cqAPI.get("/settings/subscriptions/bundles");
    setBundles(response?.data?.bundles ?? []);
  };

  const fetchProducts = async () => {
    const response = await cqAPI.get("/settings/subscriptions");
    setProducts(response?.data?.availableProducts ?? []);
  };

  useEffect(() => {
    fetchAddons();
    fetchBundles();
    fetchProducts();
  }, []);

  const handleSaveAddon = async () => {
    if (!newAddon.name || !newAddon.description) {
      toast({
        title: "Validation Error",
        description: "Name and description cannot be empty.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    if (newAddon.items.length === 0) {
      toast({
        title: "Validation Error",
        description: "At least one product must be added.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    for (const item of newAddon.items) {
      if (!item.product || !item.price) {
        toast({
          title: "Validation Error",
          description: "All products must have a selected price.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
    }

    try {
      const response = await cqAPI.post("/settings/subscriptions/addons", newAddon);
      if (response.type === "error") {
        toast({
          title: "Error",
          description: response.msg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      setNewAddon({ name: "", description: "", items: [], bundles: [] }); // Reset the modal values
      await fetchAddons();
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occurred while saving the addon.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleAddProduct = () => {
    setNewAddon((prevAddon) => ({
      ...prevAddon,
      items: [...prevAddon.items, { product: "", price: "" }],
    }));
  };

  const handleRemoveProduct = (index) => {
    setNewAddon((prevAddon) => ({
      ...prevAddon,
      items: prevAddon.items.filter((_, i) => i !== index),
    }));
  };

  const handleProductChange = (index, field, value) => {
    const updatedItems = newAddon.items.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    setNewAddon((prevAddon) => ({ ...prevAddon, items: updatedItems }));
  };

  const handleSave = async (addonId, field, newValue) => {
    const response = await cqAPI.put("/settings/subscriptions/bundles", {
      _id: addonId,
      field,
      value: newValue,
    });
    if (response.type === "error") {
      toast({
        title: "Error",
        description: response.msg,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return false;
    }
    setAddons(
      addons.map((addon) => {
        if (addon._id !== addonId) {
          return addon;
        }
        return {
          ...addon,
          [field]: newValue,
        };
      })
    );
    return true;
  };

  // SS-4462: licensed tiered prices count too, at a quantity of one
  const calculateFixedPrice = (addon) => {
    let total = 0;
    addon.items.forEach(([_product, price]) => {
      total += adminFixedPriceOf(price);
    });
    return total;
  };

  return (
    <Box
      p={4}
      overflowY="auto"
      maxHeight="100vh"
      width="100%"
      position="relative"
    >
      <Flex justifyContent="flex-end" alignItems="center" mb={4}>
        <Button leftIcon={<AddIcon />} onClick={onOpen}>
          Add Addon
        </Button>
      </Flex>
      <SimpleGrid columns={{ base: 1, md: 3, lg: 3 }} spacing="20px">
        {addons.map((addon, index) => (
          <Box
            key={index}
            borderWidth="1px"
            borderRadius="lg"
            mb={4}
            width="100%"
          >
            <Box bg="blue.500" color="white" p={4} borderTopRadius="lg">
              <EditableText
                initialValue={addon.name}
                onSave={(e) => handleSave(addon._id, "name", e)}
                showPlaceholderInDisplayMode={false}
                placeholder="Name"
                pattern={/^.{1,15}$/}
                invalid="The name can be up to 15 characters and cannot be empty."
              />
            </Box>
            <Box p={4}>
              <Flex justifyContent="space-between" alignItems="left">
                <EditableText
                  initialValue={addon.description}
                  onSave={(e) => handleSave(addon._id, "description", e)}
                  showPlaceholderInDisplayMode={false}
                  placeholder="Description"
                  multiline={true}
                  pattern={/^.{1,55}$/}
                  invalid="The description can be up to 55 characters and cannot be empty."
                />
              </Flex>
              <Flex mt={4} alignItems="center" justifyContent="space-between">
                <Text>Private</Text>
                <Switch
                  isChecked={addon.visible_to_admins_only}
                  onChange={(event) => {
                    handleSave(
                      addon._id,
                      "visible_to_admins_only",
                      event.target.checked
                    );
                  }}
                />
              </Flex>
              <Flex mt={4} alignItems="center" justifyContent="space-between">
                <Text>Active</Text>
                <Switch
                  isChecked={addon.active}
                  onChange={(event) => {
                    handleSave(addon._id, "active", event.target.checked);
                  }}
                />
              </Flex>
              {/* Display Compatible Bundles */}
              <Box mt={4}>
                <Text fontWeight="bold">Compatible Bundles:</Text>
                {addon.bundles && addon.bundles.length > 0 ? (
                  addon.bundles.map((bundleId) => {
                    const bundle = bundles.find((b) => b._id === bundleId);
                    return bundle ? (
                      <Text key={bundle._id}>• {bundle.name}</Text>
                    ) : null;
                  })
                ) : (
                  <Text>No bundles</Text>
                )}
              </Box>
              <Box mt={4}>
                <Text fontWeight="bold">Products and Prices:</Text>
                {addon.items.map(([product, price], itemIndex) => (
                  <Box key={itemIndex} mt={2}>
                    <Text>
                      {product.name} -{" "}
                      {adminPriceLabel(product, price, { addon: true })}
                    </Text>
                    {isLicensedPrice(price) && isTieredPrice(price) && (
                      <Box ml={4} className="licensed-tiers">
                        <Text>{describeLicensedTiers(price, product.unit_label)}</Text>
                      </Box>
                    )}
                    {price.billing_scheme === "tiered" && !isLicensedPrice(price) &&
                      price.tiers.map((tier, tierIndex) => (
                        <Box key={tierIndex} ml={4}>
                          <Text>
                            {`Tier ${tierIndex + 1}: $${
                              tier.unit_amount / 100
                            } per ${product.unit_label} `}
                            {tier.up_to !== null
                              ? `between ${
                                  tierIndex === 0
                                    ? 0
                                    : price.tiers[tierIndex - 1]?.up_to + 1
                                } and ${tier.up_to} ${product.unit_label}`
                              : `Anything more or equal than ${
                                  tierIndex === 0
                                    ? 0
                                    : price.tiers[tierIndex - 1]?.up_to + 1
                                }`}
                          </Text>
                        </Box>
                      ))}
                  </Box>
                ))}
              </Box>
              <Box mt={4}>
                <Text fontWeight="bold">
                  Fixed Price: ${calculateFixedPrice(addon)}
                </Text>
              </Box>
            </Box>
          </Box>
        ))}
      </SimpleGrid>

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add New Addon</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl id="addon-name" mb={4}>
              <FormLabel>Name</FormLabel>
              <Input
                value={newAddon.name}
                onChange={(e) =>
                  setNewAddon({ ...newAddon, name: e.target.value })
                }
                placeholder="Addon Name"
              />
            </FormControl>
            <FormControl id="addon-description" mb={4}>
              <FormLabel>Description</FormLabel>
              <Input
                value={newAddon.description}
                onChange={(e) =>
                  setNewAddon({ ...newAddon, description: e.target.value })
                }
                placeholder="Addon Description"
              />
            </FormControl>

            {/* Bundles Field */}
            <FormControl id="addon-bundles" mb={4}>
              <FormLabel>Bundles</FormLabel>
              <Select
                isMulti
                value={bundles
                  .filter((bundle) => newAddon.bundles.includes(bundle._id))
                  .map((bundle) => ({ value: bundle._id, label: bundle.name }))}
                onChange={(selectedOptions) => {
                  const selectedValues = selectedOptions
                    ? selectedOptions.map((option) => option.value)
                    : [];
                  setNewAddon({ ...newAddon, bundles: selectedValues });
                }}
                options={bundles.map((bundle) => ({
                  value: bundle._id,
                  label: bundle.name,
                }))}
                placeholder="Select Bundles"
              />
            </FormControl>

            {newAddon.items.map((item, index) => {
              const product = products.find((p) => p.id === item.product);
              return (
                <Flex key={index} mb={4} alignItems="center">
                  <FormControl flex="1" mr={2}>
                    <FormLabel>Product</FormLabel>
                    <ChakraSelect
                      value={item.product}
                      onChange={(e) =>
                        handleProductChange(index, "product", e.target.value)
                      }
                    >
                      <option value="">Select Product</option>
                      {products
                        .filter(
                          (p) =>
                            !newAddon.items.some(
                              (i) => i.product === p.id
                            ) || p.id === item.product
                        )
                        .map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                    </ChakraSelect>
                  </FormControl>
                  <FormControl flex="1" ml={2}>
                    <FormLabel>Pricing</FormLabel>
                    <ChakraSelect
                      value={item.price}
                      onChange={(e) =>
                        handleProductChange(index, "price", e.target.value)
                      }
                      isDisabled={!item.product}
                    >
                      <option value="">Select Pricing</option>
                      {product &&
                        product.plans.map((plan) => (
                          <option key={plan.id} value={plan.id}>
                            {plan.nickname ? `${plan.nickname}-` : ""}
                            {plan.billing_scheme === "tiered"
                              ? `$${plan.tiers[0].unit_amount / 100} per ${
                                  product.unit_label
                                }`
                              : isPerLocationAddonPrice(plan)
                                ? perLocationPriceLabel(plan)
                                : `$${plan.amount / 100} per ${
                                  product.unit_label
                                }`}
                          </option>
                        ))}
                    </ChakraSelect>
                  </FormControl>
                  <IconButton
                    icon={<DeleteIcon />}
                    onClick={() => handleRemoveProduct(index)}
                    ml={2}
                    alignSelf="flex-end"
                  />
                </Flex>
              );
            })}
            <Button
              onClick={handleAddProduct}
              leftIcon={<AddIcon />}
              colorScheme="teal"
              mb={4}
            >
              Add Product
            </Button>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose} mr={3}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSaveAddon}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default Addons;
